<template>
  <div
    ref="inputWrapper"
    class="px-2 py-4.5 w-full flex items-center border border-transparent rounded-lg relative z-10 transition-all duration-200 min-h-14"
    :class="{'mt-6': label && label !== '', 'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    style="box-shadow: 0 5px 10px rgba(31, 31, 31, 0.12)"
    :style="(disabled ? {pointerEvents: 'none'} : {}, {marginBottom: errHeight + 'px'})"
  >
    <div v-if="label && label !== ''" class="-top-6 absolute left-0">
      <t-text :size="'sm'" class="font-semibold text-black">{{ label }}</t-text>
    </div>
    <input
      ref="input"
      :disabled="disabled"
      v-model="inputVal"
      :value="value ? value : ''"
      @input="input()"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
      @change="change"
      :type="inputType"
      :placeholder="placeholder"
      :min="min"
      :max="max"
      :step="step"
      :inputmode="inputmode"
      :autocomplete="autocomplete"
      class="text-16 w-full leading-5 text-black"
      :class="{'text-gray': disabled, 'bg-white': theme === 'light', 'bg-silver': theme === 'dark'}"
    />
    <div ref="rightSide" class="right-2 bottom-4 absolute cursor-pointer">
      <slot name="right-side"></slot>
    </div>
    <t-text
      :size="'md'"
      v-show="handleErrors"
      ref="error"
      class="absolute left-0 ml-2"
      :color="'red'"
      :style="{bottom: `-${errHeight}px`}"
      >{{ fieldError }}</t-text
    >
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  export default {
    name: 'line-input',
    props: {
      label: String,
      placeholder: {
        type: String,
        required: true
      },
      pattern: {
        type: String,
        default: ''
      },
      inputType: String,
      inputColor: {
        type: String,
        required: false,
        default: 'rgb(255, 255, 255)'
      },
      autocomplete: String,
      value: String,
      min: [String, Number],
      max: [String, Number],
      step: [String, Number],
      handleErrors: {
        type: Boolean,
        required: false,
        default: true
      },
      disabled: Boolean,
      preventAutoFocus: Boolean,
      inputmode: {
        type: String,
        validator: v => ['none', 'numeric', 'tel', 'decimal', 'email', 'url', 'search'].indexOf(v) !== -1
      }
    },
    data() {
      return {
        inputVal: this.value ? this.value : '',
        interval: null,
        fieldError: '',
        errHeight: 0,
        isValid: true
      }
    },
    async mounted() {
      await this.componentLoaded()

      this.$refs.input.style.paddingRight = this.$refs.rightSide.clientWidth + 'px'
      if (this.inputType === 'number') {
        this.$refs.input.addEventListener('keypress', e => this.secureNumberInput(e))
      }

      if (!this.preventAutoFocus && this.$refs.input.value !== '') this.refs.input.focus()

      setTimeout(() => {
        if (!this.preventAutoFocus && this.$refs.input.value !== '') this.refs.input.focus()
      }, 100)

      this.interval = setInterval(() => {
        let style = window.getComputedStyle(this.$refs.input)

        if (style && (style.backgroundColor !== this.inputColor || style.backgroundImage !== 'none')) {
          this.$emit('isAutofilled')
          clearInterval(this.interval)
        }
      }, 333)
    },
    methods: {
      secureNumberInput(e) {
        if ((e.which !== 8 && e.which !== 0 && e.which !== 13 && e.which < 48) || e.which > 57) {
          e.preventDefault()
        }
      },
      async componentLoaded() {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (this.$refs.input && this.$refs.inputWrapper && this.$refs.rightSide && this.$refs.error) {
              clearInterval(interval)
              resolve()
            }
          }, 10)
        })
      },
      input() {
        if (this.pattern !== '') {
          const regex = new RegExp(this.pattern.replace(/'/g, ''))
          if (!regex.test(this.getValue())) {
            this.isValid = false
          } else {
            this.clearError()
          }
        }
        this.$emit('input', this.getValue())
      },
      change($event) {
        const value = $event.target.value

        if (this.inputType === 'number' && this.min !== null && this.max !== null && this.min >= 0 && this.max >= 0) {
          const notEnough = value < this.min
          const tooMuch = value > this.max
          const err = notEnough ? `The minimum value is ${this.min}!` : tooMuch ? `The maximum value is ${this.max}!` : ''

          if (err) this.$toast.error(err)

          $event.target.value = notEnough ? +this.min : tooMuch ? +this.max : value
        } else {
          $event.target.value = value
        }

        this.$emit('change', $event.target.value)
      },
      getValue() {
        return this.$refs.input.value
      },
      setError(err) {
        const span = document.createElement('span')
        span.textContent = err
        span.style.cssText = 'word-break: break-word'
        const height = this.$helpers.getNodeHeight([span], this.$refs.input.clientWidth)
        this.$refs.error.$el.style.bottom = 0 - height + 'px'
        this.$refs.error.$el.style.maxWidth = this.$refs.input.clientWidth + 'px'
        this.$refs.inputWrapper.style.marginBottom = height + 'px'
        this.$refs.inputWrapper.classList.add('not-valid')
        this.fieldError = err
        setTimeout(() => {
          this.errHeight = this.$refs.error.$el.clientHeight + 8
        }, 0)
        this.isValid = false
      },
      hasError() {
        return this.fieldError !== ''
      },
      clearError() {
        this.fieldError = ''
        this.errHeight = 0
        this.$refs.inputWrapper.style.marginBottom = ''
        this.$refs.inputWrapper.classList.remove('not-valid')
        this.isValid = true
      }
    },
    computed: {
      ...mapGetters(['themeColorRGBA', 'theme'])
    },
    watch: {
      value(newVal) {
        this.inputVal = newVal
        if (!this.preventAutoFocus && (this.inputVal !== null || this.inputVal !== '')) this.$refs.input.focus()
        if (this.$refs.inputWrapper.classList.contains('disabled-input') && (this.inputVal == null || this.inputVal == ''))
          this.$refs.input.blur()
      },
      isValid(newVal) {
        if (newVal) {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-red', 'border-transparent'], 'border-green')
            this.$refs.label.classList.replace(['text-red', 'text-gray'], 'text-green')
          }, 0)
        } else {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-green', 'border-transparent'], 'border-red')
            this.$refs.label.classList.replace(['text-gray', 'text-green'], 'text-red')
          }, 0)
        }
      }
    },
    beforeDestroy() {
      if (this.inputType === 'number') {
        this.$refs.input.removeEventListener('keypress', e => this.secureNumberInput(e))
      }
    },
    destroyed() {
      clearInterval(this.interval)
    }
  }
</script>
